import { PageBody } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { ErrorState } from '@pypestream/ui-patterns';
import { FC } from 'react';
import { useNavigate } from 'react-router';
import { useProStudioRoutes } from '../hooks/use-pro-studio-routes';

/**
 * The page for an address no route matches. Without it the router answers with
 * its own default error screen ("Unexpected Application Error! 404 Not Found",
 * addressed to a developer).
 *
 * LIMITATION: only addresses of three or more segments get here. A one- or
 * two-segment address matches the `:projectId` and `:projectId/:botId` schemas
 * in `use-pro-studio-routes.tsx`, so a typo there is read as a project or a
 * solution that does not exist, not as an unknown page.
 *
 * The action stays inside Pro Studio and leads to `routes.home`. The
 * access-denied screen in `Root` does not: it is shown when access is denied,
 * and calls `handleGoHome`, a full page navigation to the Manager URL
 * (design-system `go-home.ts`). Here the address is merely wrong.
 */
export const NotFound: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const homePath = useProStudioRoutes().home.path;

  return (
    <PageBody background="none">
      <ErrorState
        testId="page-not-found"
        label={t('pro-studio/common:notFound.title') || ''}
        body={t('pro-studio/common:notFound.body') || ''}
        actionTestId="page-not-found-home-btn"
        actionLabel={t('pro-studio/common:errorBoundary.goHome') || ''}
        onAction={() => navigate(homePath)}
      />
    </PageBody>
  );
};
